import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as CategoryCreators } from 'store/categories/categories-duck.js';
import { Button, IconButton, Typography } from '@mui/material';
import CollapsibleOrderableTable from 'components/collapsible-orderable-table/index.jsx';
import AddIcon from '@mui/icons-material/Add';
import CategoryModal from './category-modal/index.jsx';
import SubCategoryModal from './sub-category-modal/index.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation.js';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import DeleteConfirmationModal from 'components/delete-confirmation-modal/index.jsx';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';

const CategoriesManagementPage = ({
  categories,
  categoriesOpenModal,
  subCategoriesOpenModal,
  categoriesFetchAsync,
  categoriesUpdateOrder,
  categoriesDeleteAsync,
  categoriesUpdateOrdersAsync,
  subCategoriesUpdateOrder,
  subCategoriesDeleteAsync,
}) => {
  const {
    isOpen,
    message,
    open: openDeleteModal,
    close: closeDeleteModal,
    confirm: confirmDelete,
  } = useDeleteConfirmation();

  const fetchCategories = useCallback(() => {
    categoriesFetchAsync();
  }, [categoriesFetchAsync]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleChangeOrder = (oldIndex, newIndex) => {
    categoriesUpdateOrder(oldIndex, newIndex);
  };

  const handleChangeSubCategoriesOrder = (oldIndex, newIndex, parentId) => {
    subCategoriesUpdateOrder(oldIndex, newIndex, parentId);
  };

  const columns = [
    { title: 'Nome', key: 'name', width: '30%', align: 'left' },
    {
      title: 'Mostrar no Menu',
      key: 'showOnMenu',
      width: '10%',
      align: 'center',
      render: (item) => (item.showOnMenu ? <CheckIcon color="success" /> : <BlockIcon />),
    },
    {
      title: 'Ações',
      width: '55%',
      align: 'right',
      key: 'actions',
      render: (item) => (
        <>
          <IconButton>
            <EditSquareIcon onClick={() => categoriesOpenModal(item)} />
          </IconButton>
          <IconButton
            onClick={() =>
              openDeleteModal(
                () => categoriesDeleteAsync(item.id),
                'Deseja remover esta categoria?',
              )
            }
            color="error"
          >
            <DeleteIcon />
          </IconButton>
          <IconButton onClick={() => subCategoriesOpenModal(item.id, null)} color="primary">
            <AddIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const subColumns = [
    { title: 'Nome', key: 'name', width: '30%', align: 'left' },
    {
      title: 'Ações',
      width: '70%',
      align: 'right',
      key: 'actions',
      render: (item) => (
        <>
          <IconButton>
            <EditSquareIcon onClick={() => subCategoriesOpenModal(null, item)} />
          </IconButton>
          <IconButton
            onClick={() =>
              openDeleteModal(
                () => subCategoriesDeleteAsync(item.id),
                'Deseja remover esta subcategoria?',
              )
            }
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <CategoryModal />
      <SubCategoryModal />
      <DeleteConfirmationModal
        isOpen={isOpen}
        message={message}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
      />

      <Typography variant="h5" gutterBottom>
        Gestão de Categorias
      </Typography>
      <Button
        color="primary"
        variant="contained"
        onClick={() => categoriesOpenModal({ id: null, name: '' })}
        sx={{ mb: 2 }}
      >
        <AddIcon /> Adicionar Categoria
      </Button>
      <CollapsibleOrderableTable
        columns={columns}
        subColumns={subColumns}
        subItemsDescription="Sub-Categorias"
        data={categories}
        subItemsKey="subCategories"
        rowKey="id"
        onChangeOrder={handleChangeOrder}
        onChangeNestedOrder={handleChangeSubCategoriesOrder}
        onSaveOrderChange={categoriesUpdateOrdersAsync}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  categories: state.Category.categories,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(CategoryCreators, dispatch);
CategoriesManagementPage.propTypes = {
  categories: PropTypes.array.isRequired,
  categoriesOpenModal: PropTypes.func.isRequired,
  categoriesFetchAsync: PropTypes.func.isRequired,
  categoriesUpdateOrder: PropTypes.func.isRequired,
  categoriesDeleteAsync: PropTypes.func.isRequired,
  subCategoriesUpdateOrder: PropTypes.func.isRequired,
  subCategoriesOpenModal: PropTypes.func.isRequired,
  subCategoriesDeleteAsync: PropTypes.func.isRequired,
  categoriesUpdateOrdersAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(CategoriesManagementPage);
